import { ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowDownTrayIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import { downloadFileFromUrl } from "@utils/helpers";
import React, { FC, useMemo, useRef, useState } from "react";
import { read as xlsxRead } from "xlsx";

import {
  Alert,
  Button,
  Dialog,
  Headline,
  Input,
  Paragraph,
  Select,
  SelectItem,
  Small,
} from "@fronterahealth/frontera-ui-components";

import {
  useArchiveGoalBankCurriculumMutation,
  useExportGoalBankCurriculumMutation,
  useGetCurriculumsQuery,
  useGetGoalBankUploadUrlMutation,
  useImportGoalBankMutation,
} from "@api/graphql/types-and-hooks";
import { useGoalBankFolders } from "@components/GoalsBank/hooks/useGoalBankFolders";
import { ToggleSwitch } from "@components/ToggleSwitch/ToggleSwitch";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import {
  ImportStats,
  countGoalRowsSkipped,
  downloadGoalBankImportTemplate,
  hasImportableChanges,
  parseStats,
  resolveImportButtonLabel,
  resolveSkipRowLabel,
} from "@pages/GoalBankImport/goalBankImport.helpers";

interface ImportPreview {
  s3Key: string;
  sheet: string;
  curriculumName: string;
  triggerEmbeddings: boolean;
  stats: ImportStats;
}

export const GoalBankImportPage: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<SelectItem | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<SelectItem | null>(null);
  const [isAddingNewCurriculum, setIsAddingNewCurriculum] = useState(false);
  const [newCurriculumName, setNewCurriculumName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Locked on: goal suggestions are always generated on import. Shown read-only for transparency.
  const triggerEmbeddings = true;
  const [curriculumToArchive, setCurriculumToArchive] = useState<{ id: string; name: string } | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [isConfirmingImport, setIsConfirmingImport] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: curriculaData, isLoading: isLoadingCurricula } = useGetCurriculumsQuery({});
  const { mutateAsync: getUploadUrl } = useGetGoalBankUploadUrlMutation({});
  const { mutateAsync: importGoalBank } = useImportGoalBankMutation({});
  const { mutateAsync: archiveCurriculum, isPending: isArchiving } = useArchiveGoalBankCurriculumMutation({});
  const { mutateAsync: exportCurriculum } = useExportGoalBankCurriculumMutation({});

  const curricula = curriculaData?.getCurricula ?? [];
  const curriculumItems: SelectItem[] = curricula.map((c) => ({ primary: c.name, id: c.id }));

  // Folder + goal counts per curriculum, so archiving shows what's being hidden.
  const { orgFolders } = useGoalBankFolders(undefined, true);
  const countsByCurriculum = useMemo(() => {
    const map = new Map<string, { folders: number; goals: number }>();
    orgFolders.forEach((f) => {
      if (!f.curriculumId) return;
      const entry = map.get(f.curriculumId) ?? { folders: 0, goals: 0 };
      entry.folders += 1;
      entry.goals += f.count ?? 0;
      map.set(f.curriculumId, entry);
    });
    return map;
  }, [orgFolders]);
  const archiveCounts = curriculumToArchive ? countsByCurriculum.get(curriculumToArchive.id) : undefined;

  // Detect when the chosen import target collides with an existing (active) goal bank.
  const targetCurriculumName = (isAddingNewCurriculum ? newCurriculumName : selectedCurriculum?.primary)?.trim();
  const mergeTarget =
    selectedFile && targetCurriculumName
      ? curricula.find((c) => c.name.toLowerCase() === targetCurriculumName.toLowerCase())
      : undefined;

  const parseSheetNames = async (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = xlsxRead(data, { type: "array" });
          resolve(workbook.SheetNames);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelected = async (file: File) => {
    if (!file.name.match(/\.xlsx$/i)) {
      notifyError("Please upload an Excel file (.xlsx)");
      return;
    }
    setSelectedFile(file);
    setSelectedSheet(null);
    try {
      const names = await parseSheetNames(file);
      setSheetNames(names);
      if (names.length === 1) {
        setSelectedSheet({ primary: names[0] });
      }
    } catch {
      notifyError("Could not read sheet names from this file. Please verify it is a valid spreadsheet.");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  // Step 1: upload the file and run a dry-run so the user can confirm counts
  // (found vs skipped) before anything is written.
  const handleImport = async () => {
    if (!selectedFile || !selectedSheet || !targetCurriculumName) return;

    const curriculumName = targetCurriculumName;

    setIsUploading(true);
    const contentType = selectedFile.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    try {
      const uploadUrlResult = await getUploadUrl({
        fileName: selectedFile.name,
        contentType,
      });

      const { signedUrl, s3Key } = uploadUrlResult.getGoalBankUploadUrl ?? {};
      if (!signedUrl || !s3Key) throw new Error("Failed to get upload URL");

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: selectedFile,
      });
      if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.statusText}`);

      const result = await importGoalBank({
        input: {
          s3Key,
          sheet: selectedSheet.primary,
          curriculumName,
          triggerEmbeddings,
          dryRun: true,
        },
      });

      if (result.importGoalBank?.status) {
        setImportPreview({
          s3Key,
          sheet: selectedSheet.primary,
          curriculumName,
          triggerEmbeddings,
          stats: parseStats(result.importGoalBank.stats),
        });
      } else {
        notifyError(result.importGoalBank?.message || "Could not read this spreadsheet.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Import failed. Please try again.";
      notifyError(message);
    } finally {
      setIsUploading(false);
    }
  };

  // Step 2: commit the real import, reusing the already-uploaded file.
  const handleConfirmImport = async () => {
    if (!importPreview) return;
    setIsConfirmingImport(true);
    try {
      const result = await importGoalBank({
        input: {
          s3Key: importPreview.s3Key,
          sheet: importPreview.sheet,
          curriculumName: importPreview.curriculumName,
          triggerEmbeddings: importPreview.triggerEmbeddings,
          dryRun: false,
        },
      });

      if (result.importGoalBank?.status) {
        notifySuccess("Goal bank imported successfully!");
        await queryClient.invalidateQueries({ queryKey: ["goalBankFolders"] });
        await queryClient.invalidateQueries({ queryKey: ["GetCurriculums"] });
        setImportPreview(null);
        // Return to the upload loader after a successful import.
        resetFile();
        setSelectedCurriculum(null);
        setIsAddingNewCurriculum(false);
        setNewCurriculumName("");
      } else {
        notifyError(result.importGoalBank?.message || "Import failed. Please try again.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Import failed. Please try again.";
      notifyError(message);
    } finally {
      setIsConfirmingImport(false);
    }
  };

  const handleDownload = async (curriculumId: string, curriculumName: string) => {
    setDownloadingId(curriculumId);
    try {
      const result = await exportCurriculum({ curriculumId });
      const url = result.exportGoalBankCurriculum?.signedUrl;
      if (!url) throw new Error("No download link returned.");
      const fileName = result.exportGoalBankCurriculum?.fileName || `${curriculumName}.xlsx`;
      await downloadFileFromUrl({ url, fileName });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not export this goal bank. Please try again.";
      notifyError(message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleArchive = async () => {
    if (!curriculumToArchive) return;
    try {
      const result = await archiveCurriculum({ curriculumId: curriculumToArchive.id, archived: true });
      if (result.archiveGoalBankCurriculum?.status) {
        notifySuccess(`Archived "${curriculumToArchive.name}".`);
        await queryClient.invalidateQueries({ queryKey: ["goalBankFolders"] });
        await queryClient.invalidateQueries({ queryKey: ["GetCurriculums"] });
        await queryClient.invalidateQueries({ queryKey: ["orgHasGoalBankEmbeddings"] });
      } else {
        notifyError(result.archiveGoalBankCurriculum?.message || "Could not archive this goal bank.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not archive this goal bank. Please try again.";
      notifyError(message);
    } finally {
      setCurriculumToArchive(null);
    }
  };

  const resetFile = () => {
    setSelectedFile(null);
    setSheetNames([]);
    setSelectedSheet(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sheetItems: SelectItem[] = sheetNames.map((name) => ({ primary: name }));

  const canImport = selectedFile && selectedSheet && !!targetCurriculumName && !isUploading;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-6">
      <div>
        <Headline size="medium">Goal Bank</Headline>
        <Paragraph colorType="secondary" className="mt-1">
          Upload a spreadsheet to import goals into your organization's goal bank. The file should contain columns for
          Folder Name, Goal Name, and Mastery Criteria. Optionally include a second sheet named &ldquo;Targets&rdquo;
          with columns for Folder Name, Goal Name, Target Name, and Mastery Criteria to also bulk-import targets under
          those goals.
        </Paragraph>
        <button type="button" className="mt-1 text-sm text-rio-600 underline" onClick={downloadGoalBankImportTemplate}>
          Download a blank template
        </button>
      </div>

      {/* File upload area */}
      {!selectedFile ? (
        <div
          className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 transition-colors ${
            isDragging ? "border-rio-400 bg-rio-50" : "border-limestone-300 bg-limestone-50 hover:border-limestone-400"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <ArrowUpTrayIcon className="mb-3 h-10 w-10 text-limestone-400" />
          <Small displayType="loud" className="text-limeston-700">
            Drop your spreadsheet here or <span className="text-rio-600 underline">browse</span>
          </Small>
          <Small colorType="secondary" className="mt-1">
            Accepts .xlsx
          </Small>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelected(file);
            }}
          />
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-limestone-200 bg-white p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <DocumentIcon className="h-6 w-6 text-limestone-500" />
            <div>
              <Small displayType="loud">{selectedFile.name}</Small>
              <Small colorType="secondary">{(selectedFile.size / 1024).toFixed(1)} KB</Small>
            </div>
          </div>
          <button onClick={resetFile} className="rounded-full p-1 hover:bg-limestone-100">
            <XMarkIcon className="h-5 w-5 text-limestone-500" />
          </button>
        </div>
      )}

      {/* Sheet selector – only shown after file is picked */}
      {selectedFile && sheetNames.length > 0 && (
        <Select
          title="Sheet"
          placeholderText="Select a sheet to import"
          items={sheetItems}
          selected={selectedSheet}
          setSelected={setSelectedSheet}
        />
      )}

      {/* Curriculum / Folder Category */}
      {selectedFile && (
        <div className="flex flex-col gap-2">
          {!isAddingNewCurriculum ? (
            <Select
              title="Folder Category"
              placeholderText="Select a goal bank"
              items={curriculumItems}
              selected={selectedCurriculum}
              setSelected={setSelectedCurriculum}
              isLoading={isLoadingCurricula}
              actionItem={{
                label: "+ Add New",
                onClick: () => {
                  setIsAddingNewCurriculum(true);
                  setSelectedCurriculum(null);
                },
                closeDropdown: true,
              }}
            />
          ) : (
            <div className="flex flex-col gap-2">
              <Input
                name="new-curriculum"
                label="Folder Category"
                value={newCurriculumName}
                onChange={(e) => setNewCurriculumName(e.target.value)}
                placeholder="Enter a new folder category name"
              />
              <button
                className="self-start text-sm text-rio-600 underline"
                onClick={() => {
                  setIsAddingNewCurriculum(false);
                  setNewCurriculumName("");
                }}
              >
                Pick from existing instead
              </button>
            </div>
          )}
          <Paragraph colorType="secondary" className="text-sm">
            Required. Groups imported goals into a goal bank you can manage (archive, export, restore).
          </Paragraph>
          {mergeTarget && (
            <Alert
              appearance="warn"
              title={`"${mergeTarget.name}" already exists`}
              text="These goals will be added to the existing goal bank. Existing goals are kept and duplicates are skipped."
              className="rounded-lg"
            />
          )}
        </div>
      )}

      {selectedFile && (
        <div className="flex items-center justify-between gap-4">
          <div>
            <Small displayType="loud">Generate goal suggestions</Small>
            <Small colorType="secondary" className="block">
              Build smart suggestions from these goals so similar goals are recommended automatically when writing
              reports.
            </Small>
          </div>
          <ToggleSwitch enabled={triggerEmbeddings} setEnabled={() => undefined} disabled />
        </div>
      )}

      {/* Import button */}
      {selectedFile && (
        <Button
          appearance="primary"
          text={isUploading ? "Importing..." : "Import Goals"}
          disabled={!canImport}
          onClick={handleImport}
        />
      )}

      {/* Existing goal banks: archive */}
      {curricula.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-limestone-200 pt-6">
          <Small displayType="loud">Existing goal banks</Small>
          <Paragraph colorType="secondary">
            Archive a goal bank to hide it from goal pickers and imports before loading a replacement. An administrator
            can restore it later.
          </Paragraph>
          <div className="flex flex-col divide-y divide-limestone-100 rounded-lg border border-limestone-200 bg-white">
            {curricula.map((c) => {
              const counts = countsByCurriculum.get(c.id);
              return (
                <div key={c.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex flex-col">
                    <Small displayType="loud">{c.name}</Small>
                    {counts && (
                      <Small colorType="secondary">
                        {counts.folders} folders · {counts.goals} goals
                      </Small>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleDownload(c.id, c.name)}
                      disabled={downloadingId !== null}
                      className="text-text-primary hover:text-text-secondary animate-in-fade-left"
                      title="Download as spreadsheet"
                      data-testid="download-goal-bank-button"
                    >
                      {downloadingId === c.id ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin-slow text-text-secondary" />
                      ) : (
                        <ArrowDownTrayIcon className="h-5 w-5 cursor-pointer" />
                      )}
                    </button>
                    <Button
                      appearance="secondary"
                      text="Archive"
                      onClick={() => setCurriculumToArchive({ id: c.id, name: c.name })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog
        size="md"
        open={!!curriculumToArchive}
        setOpen={(open) => {
          if (!open) setCurriculumToArchive(null);
        }}
        title="Archive goal bank"
        primaryButton={{
          text: isArchiving ? "Archiving..." : "Archive",
          buttonAction: "destructive",
          onClick: handleArchive,
          disabled: isArchiving,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setCurriculumToArchive(null),
        }}
      >
        <Paragraph colorType="secondary">
          Archive &ldquo;{curriculumToArchive?.name}&rdquo;
          {archiveCounts ? ` (${archiveCounts.folders} folders, ${archiveCounts.goals} goals)` : ""}? It will be hidden
          from goal pickers and imports. This does not affect existing reports and can be restored by an administrator.
        </Paragraph>
      </Dialog>

      <Dialog
        size="md"
        open={!!importPreview}
        setOpen={(open) => {
          if (!open && !isConfirmingImport) setImportPreview(null);
        }}
        title="Confirm import"
        primaryButton={{
          text: isConfirmingImport
            ? "Importing..."
            : (importPreview && resolveImportButtonLabel(importPreview.stats)) || "Import goals",
          buttonAction: "regular",
          onClick: handleConfirmImport,
          disabled: isConfirmingImport || !(importPreview && hasImportableChanges(importPreview.stats)),
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setImportPreview(null),
          disabled: isConfirmingImport,
        }}
      >
        {importPreview && (
          <div className="flex flex-col gap-3">
            <Paragraph colorType="secondary">
              Found <span className="font-semibold">{importPreview.stats.goal_banks_created ?? 0}</span> new goal
              {(importPreview.stats.goal_banks_created ?? 0) === 1 ? "" : "s"} to import
              {importPreview.curriculumName ? ` into "${importPreview.curriculumName}"` : ""}.
            </Paragraph>
            <div className="flex flex-col gap-1">
              {!!importPreview.stats.goal_banks_skipped && (
                <Small colorType="secondary">
                  {importPreview.stats.goal_banks_skipped} duplicate goal
                  {importPreview.stats.goal_banks_skipped === 1 ? "" : "s"} already in this goal bank (skipped)
                </Small>
              )}
              {!!importPreview.stats.goal_banks_updated && (
                <Small colorType="secondary">
                  {importPreview.stats.goal_banks_updated} existing goal
                  {importPreview.stats.goal_banks_updated === 1 ? "" : "s"} will be updated
                </Small>
              )}
              {!!countGoalRowsSkipped(importPreview.stats) && (
                <Small colorType="secondary">
                  {countGoalRowsSkipped(importPreview.stats)} row
                  {countGoalRowsSkipped(importPreview.stats) === 1 ? "" : "s"} skipped (missing required values)
                </Small>
              )}
            </div>
            {!!(
              importPreview.stats.target_banks_created ||
              importPreview.stats.target_banks_updated ||
              importPreview.stats.target_banks_skipped
            ) && (
              <div className="flex flex-col gap-1 border-t border-limestone-100 pt-3">
                <Paragraph colorType="secondary">
                  Found <span className="font-semibold">{importPreview.stats.target_banks_created ?? 0}</span> new
                  target{(importPreview.stats.target_banks_created ?? 0) === 1 ? "" : "s"} to import.
                </Paragraph>
                {!!importPreview.stats.target_banks_skipped && (
                  <Small colorType="secondary">
                    {importPreview.stats.target_banks_skipped} duplicate target
                    {importPreview.stats.target_banks_skipped === 1 ? "" : "s"} already in this goal bank (skipped)
                  </Small>
                )}
                {!!importPreview.stats.target_banks_updated && (
                  <Small colorType="secondary">
                    {importPreview.stats.target_banks_updated} existing target
                    {importPreview.stats.target_banks_updated === 1 ? "" : "s"} will be updated
                  </Small>
                )}
              </div>
            )}
            {!!importPreview.stats.errors?.length && (
              <Alert
                appearance="warn"
                title={`${importPreview.stats.errors.length} row${importPreview.stats.errors.length === 1 ? "" : "s"} skipped`}
                text="These rows are missing required values and will not be imported."
                className="rounded-lg"
              />
            )}
            {!!importPreview.stats.errors?.length && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-limestone-200 bg-limestone-50 p-3">
                {importPreview.stats.errors.slice(0, 50).map((e) => (
                  <Small
                    key={`${e.sheet ?? ""}-${e.row ?? e.row_index ?? ""}-${e.error}`}
                    colorType="secondary"
                    className="block"
                  >
                    {resolveSkipRowLabel(e)}
                    {e.error}
                  </Small>
                ))}
                {importPreview.stats.errors.length > 50 && (
                  <Small colorType="secondary" className="block">
                    + {importPreview.stats.errors.length - 50} more
                  </Small>
                )}
              </div>
            )}
            {!hasImportableChanges(importPreview.stats) && (
              <Paragraph colorType="secondary">No new goals or targets will be added from this file.</Paragraph>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};
